




































































































































import { Component, Vue, Prop, Watch, Provide } from "vue-property-decorator";
import dayjs from "dayjs";

/* eslint-disable @typescript-eslint/camelcase */

enum TaxabilityType {
  EXEMPT = "Exempt",
  ZERO = "Zero",
  TAXABLE = "Taxable",
}

interface TaxCode {
  code: string;
  description: string;
  additionalDescription: string;
  categories: string[];
}

enum Region {
  US = "US",
  CA = "CA",
}

interface TaxabilityRule {
  taxability: string;
  jurisdiction_name: string;
  jurisdiction_code: string;
  country_code: string;
  jurisdiction_type: string;
  additional_info: string;
}

@Component
export default class TaxabilityTable extends Vue {
  @Provide("$api") $api: any;
  @Prop({ required: true }) private taxcodes!: string[];
  @Prop({ required: true }) private jurisdictions!: string[];
  @Prop({ required: true }) private region!: string;
  @Prop({ required: true }) private codeToJurisdictionTaxability!: {
    [taxCode: string]: { [jurisdiction: string]: TaxabilityRule };
  };

  taxcodeDetails = {};
  descriptionLengthLimit = 80;
  showFullDescription = false;

  @Watch("taxcodes")
  public updateTaxcodes(newTaxcodes: string[]) {
    this.taxcodes = newTaxcodes;
    this.updateTaxcodeDetails();
  }

  @Watch("jurisdictions")
  public updateJurisdictions(newJurisdictions: string[]) {
    this.jurisdictions = newJurisdictions;
  }

  @Watch("region")
  public updateRegion(newRegion: string) {
    this.region = newRegion;
  }

  @Watch("codeToJurisdictionTaxability")
  public updateCodeToJurisdictionTaxability(newMappings: {
    [taxCode: string]: { [jurisdiction: string]: TaxabilityRule };
  }) {
    this.codeToJurisdictionTaxability = newMappings;
  }

  Region = Region;

  getShortTaxcodeDescription(taxcode) {
    const description: string = this.taxcodeDetails[taxcode].description || "";
    return description.substr(0, this.descriptionLengthLimit - 15);
  }

  seeMore() {
    this.showFullDescription = true;
  }

  mounted() {
    this.updateTaxcodeDetails();
  }

  public updateTaxcodeDetails() {
    this.$api
      .post("/api/taxcodes/", this.taxcodes)
      .then((response) => {
        const taxcodesData: TaxCode[] = response.data;
        taxcodesData.forEach((taxcodeData) =>
          Vue.set(this.taxcodeDetails, taxcodeData.code, taxcodeData)
        );
        this.$store.commit("stopLoading");
      })
      .catch((error) => this.$store.commit("stopLoading"));
  }

  public onDownloadTaxability() {
    this.$api
      .post(
        "/api/taxability/",
        {
          taxcodes: this.taxcodes,
          country_codes: [this.region],
          state_codes: this.jurisdictions.map(
            (jurisdiction) =>
              this.codeToJurisdictionTaxability[this.taxcodes[0]][jurisdiction]
                .jurisdiction_code
          ),
        },
        {
          responseType: "arraybuffer",
          headers: {
            Accept:
              "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
          },
        }
      )
      .then((response) => {
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", "taxability.xlsx");
        document.body.appendChild(link);
        link.click();
        this.captureDownloadTaxability();
      })
      .catch((error) => this.$store.commit("stopLoading"));
  }

  public captureDownloadTaxability() {
    if (this.$store.getters.analyticsEnabled) {
      window._satellite.track("taxability_build_download");
    }
  }

  public getTaxabilityClass(taxCode: string, jurisdiction: string): string {
    const taxabilityRule = this.getTaxabilityByCodeAndJurisdiction(
      taxCode,
      jurisdiction
    );
    if (
      taxabilityRule.taxability == TaxabilityType.ZERO ||
      taxabilityRule.specialTax != "" ||
      taxabilityRule.additional_info != ""
    )
      return "taxability-special";
    if (taxabilityRule.taxability == TaxabilityType.EXEMPT)
      return "taxability-exempt";
    return "";
  }

  private newTaxcodeText(date) {
    return (
      "New tax code! Code will be active from " +
      dayjs(date).format("D MMM, YYYY")
    );
  }

  private isNewTaxcode(date) {
    return dayjs(date).isAfter(dayjs(new Date()));
  }

  public getTaxabilityByCodeAndJurisdiction(
    taxCode: string,
    jurisdiction: string
  ) {
    if (
      this.codeToJurisdictionTaxability[taxCode] &&
      this.codeToJurisdictionTaxability[taxCode][jurisdiction] &&
      this.codeToJurisdictionTaxability[taxCode][jurisdiction].taxability != ""
    ) {
      const taxability = this.codeToJurisdictionTaxability[taxCode][
        jurisdiction
      ].taxability;
      if (taxability === TaxabilityType.EXEMPT)
        return {
          ...this.codeToJurisdictionTaxability[taxCode][jurisdiction],
          specialTax: "",
        };
      if (
        taxability === TaxabilityType.TAXABLE ||
        taxability === TaxabilityType.ZERO
      )
        return {
          ...this.codeToJurisdictionTaxability[taxCode][jurisdiction],
          specialTax: "",
        };
      return {
        ...this.codeToJurisdictionTaxability[taxCode][jurisdiction],
        taxability: TaxabilityType.TAXABLE,
        specialTax: taxability,
      };
    }
    return {
      taxability: "",
      specialTax: "",
      jurisdiction_name: "",
      jurisdiction_code: "",
      country_code: "",
      jurisdiction_type: "",
      additional_info: "",
    };
  }
  /* eslint-enable @typescript-eslint/camelcase */
}
